import React, { MouseEventHandler, useRef, CSSProperties } from "react"
import classnames from "classnames"
import * as styles from "./touchpoint-button.module.css"
import { Link } from "gatsby"
import { SvgInline } from "./SVGInline"

type mode = "customer" | "employee"
interface TouchpointButtonProps {
  style: CSSProperties
  name: string
  logo: string
  description: string
  last?: boolean
  mode: "customer" | "employee"
  onMouseEnter: MouseEventHandler
  onMouseLeave: MouseEventHandler
  link: string
  indicatorSide: "top" | "bottom"
  first?: boolean
  descriptionSide?: "top" | "bottom"
  className?: Record<string, string>
  openInNewTab?: boolean
}

function DescriptionBox() {
  return (
    <svg
      width="175"
      height="115"
      viewBox="0 0 175 115"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g>
        <path d="M175,90V25h-37.5h-25H89.7H0v90h150.1L175,90z" />
        <path d="M101.6,0L89.7,25L101.6,0z" />
        <polyline points="89.7,25 112.5,25 101.6,0 	" />
      </g>
    </svg>
  )
}

const modeStyles: { [k in mode]: string } = {
  customer: styles.customer,
  employee: styles.employee,
}

function ShowButton({
  className,
  cur,
  description,
  descriptionSide,
  first,
  lastStyle,
  logo,
  name,
  style,
}: {
  first: boolean | undefined
  style: string
  lastStyle: any
  className: Record<string, string> | undefined
  descriptionSide: string | undefined
  name: string
  description: string
  logo: string
  cur: React.MutableRefObject<null>
}) {
  return (
    <div
      className={classnames(
        styles.button,
        first ? styles.first : "",
        style,
        lastStyle,
        className?.button
      )}
    >
      <div
        className={classnames(
          "body-font",
          className?.description,
          styles.description,
          descriptionSide === "top" ? styles.descriptionTop : ""
        )}
      >
        <DescriptionBox />
        <span className={styles.descName}>{name}</span>
        <span className={styles.desc}>{description}</span>
      </div>
      <SvgInline
        url={logo}
        className={classnames(styles.logo, className?.logo)}
      />
      <div
        className={classnames(
          "body-font",
          styles.nameContainer,
          className?.nameContainer
        )}
      >
        <div className={classnames(styles.name, className?.name)}>{name}</div>

        <div ref={cur}></div>
      </div>
    </div>
  )
}
export default function TouchpointButton({
  name,
  logo,
  description,
  last,
  first,
  mode,
  onMouseEnter,
  onMouseLeave,
  link,
  indicatorSide,
  style: styless,
  descriptionSide,
  className,
  openInNewTab = false,
}: TouchpointButtonProps) {
  const style = modeStyles[mode]
  const cur = useRef(null)
  const lastStyle = last ? styles.last : null

  const props = React.useMemo(
    () => ({
      style: styless,
      className: classnames(
        styles.buttonContainer,
        indicatorSide === "bottom" ? styles.bottom : styles.top,
        className?.buttonContainer
      ),
      onMouseEnter,
      onMouseLeave,
    }),
    [link, styless, style, indicatorSide, className, onMouseEnter, onMouseLeave]
  )

  const buttons = React.useMemo(
    () => (
      <ShowButton
        first={first}
        style={style}
        lastStyle={lastStyle}
        className={className}
        cur={cur}
        description={description}
        descriptionSide={descriptionSide}
        logo={logo}
        name={name}
      />
    ),
    [
      first,
      style,
      lastStyle,
      className,
      cur,
      description,
      descriptionSide,
      logo,
      name,
    ]
  )

  return openInNewTab ? (
    <a {...props} href={link} target="_blank" rel="noopener noreferrer">
      {buttons}
    </a>
  ) : (
    <Link to={`${link}/`} {...props}>
      {buttons}
    </Link>
  )
}
